class KleryaSocialVideoCarousel extends HTMLElement {
  constructor() {
    super();
    this.track = null;
    this.items = [];
    this.activeIndex = -1;
    this.dragState = null;
    this.scrollRaf = 0;
    this.programmaticScrollTimer = 0;
    this.suppressScrollSyncUntil = 0;
    this.ignoreClickUntil = 0;
    this.resizeObserver = null;
    this.visibilityObserver = null;
    this.isInView = false;
    this.reducedMotionQuery =
      typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    this.videoEvents = ['play', 'pause', 'volumechange', 'ended'];
    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onVideoStateChange = this.onVideoStateChange.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    this.onEditorSelect = this.onEditorSelect.bind(this);
  }

  get prefersReducedMotion() {
    return this.reducedMotionQuery ? this.reducedMotionQuery.matches : false;
  }

  connectedCallback() {
    this.track = this.querySelector('[data-social-video-track]');
    if (!this.track) return;

    this.items = Array.from(this.querySelectorAll('[data-social-video-item]'));
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.track.addEventListener('pointerdown', this.onPointerDown);
    this.track.addEventListener('keydown', this.onKeyDown);
    this.addEventListener('click', this.onClick);
    this.querySelectorAll('video').forEach((video) => {
      this.videoEvents.forEach((eventName) => video.addEventListener(eventName, this.onVideoStateChange));
    });

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.track);
    } else {
      window.addEventListener('resize', this.onResize);
    }

    if ('IntersectionObserver' in window) {
      this.visibilityObserver = new IntersectionObserver(this.onVisibilityChange, {
        rootMargin: '300px 0px',
        threshold: 0.01,
      });
      this.visibilityObserver.observe(this);
    } else {
      this.isInView = true;
    }

    // Theme Editor: focus the block a merchant selects. The event bubbles up
    // from the block element, so listening on the carousel auto-scopes it.
    this.addEventListener('shopify:block:select', this.onEditorSelect);

    window.requestAnimationFrame(() => {
      this.items.forEach((item) => this.updateVideoControls(item));
      const initial = Math.min(
        Math.max(Number(this.dataset.initialIndex || 0), 0),
        Math.max(this.items.length - 1, 0)
      );
      this.setActive(initial, { scroll: true, instant: true });
    });
  }

  disconnectedCallback() {
    if (!this.track) return;
    this.track.removeEventListener('scroll', this.onScroll);
    this.track.removeEventListener('pointerdown', this.onPointerDown);
    this.track.removeEventListener('keydown', this.onKeyDown);
    this.removeEventListener('click', this.onClick);
    this.querySelectorAll('video').forEach((video) => {
      this.videoEvents.forEach((eventName) => video.removeEventListener(eventName, this.onVideoStateChange));
    });
    this.removeEventListener('shopify:block:select', this.onEditorSelect);
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('resize', this.onResize);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.visibilityObserver) this.visibilityObserver.disconnect();
    if (this.scrollRaf) window.cancelAnimationFrame(this.scrollRaf);
    if (this.programmaticScrollTimer) window.clearTimeout(this.programmaticScrollTimer);
  }

  onVisibilityChange(entries) {
    const entry = entries[0];
    if (!entry) return;
    this.isInView = entry.isIntersecting;
    if (this.activeIndex > -1) {
      this.syncVideo(this.items[this.activeIndex], true);
    }
  }

  onEditorSelect(event) {
    if (!this.contains(event.target)) return;
    const blockId = event.detail && event.detail.blockId;
    if (!blockId) return;
    const item = this.querySelector('[data-block-id="' + blockId + '"]');
    const index = this.items.indexOf(item);
    if (index > -1) this.setActive(index, { scroll: true });
  }

  onClick(event) {
    if (Date.now() < this.ignoreClickUntil) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const nav = event.target.closest('[data-carousel-nav]');
    if (nav && this.contains(nav)) {
      event.preventDefault();
      const direction = nav.getAttribute('data-carousel-nav') === 'next' ? 1 : -1;
      this.move(direction);
      return;
    }

    const clickedItem = event.target.closest('[data-social-video-item]');
    if (clickedItem && this.contains(clickedItem)) {
      const clickedIndex = this.items.indexOf(clickedItem);
      if (clickedIndex > -1 && clickedIndex !== this.activeIndex) {
        event.preventDefault();
        event.stopPropagation();
        this.setActive(clickedIndex, { scroll: true });
        return;
      }
    }

    const actionButton = event.target.closest('[data-video-action]');
    if (!actionButton || !this.contains(actionButton)) return;

    const item = actionButton.closest('[data-social-video-item]');
    const video = item?.querySelector('video');
    if (!video) return;

    const action = actionButton.getAttribute('data-video-action');

    if (action === 'mute') {
      video.muted = !video.muted;
    }

    if (action === 'play') {
      if (video.paused) {
        item.removeAttribute('data-user-paused');
        this.playVideo(video);
      } else {
        item.setAttribute('data-user-paused', 'true');
        video.pause();
      }
    }

    this.updateVideoControls(item);
  }

  onKeyDown(event) {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft' && event.key !== 'Home' && event.key !== 'End') return;

    event.preventDefault();
    if (event.key === 'Home') {
      this.setActive(0, { scroll: true });
      return;
    }
    if (event.key === 'End') {
      this.setActive(this.items.length - 1, { scroll: true });
      return;
    }

    const direction = event.key === 'ArrowRight' ? 1 : -1;
    this.move(direction);
  }

  onVideoStateChange(event) {
    const item = event.target.closest('[data-social-video-item]');
    if (item) this.updateVideoControls(item);
  }

  onPointerDown(event) {
    // Touch and pen use the browser's native momentum scrolling and snapping;
    // only mouse needs the manual click-drag affordance.
    if (event.pointerType && event.pointerType !== 'mouse') return;
    if (event.button !== 0) return;
    if (event.target.closest('a, button, input, select, textarea, product-form')) return;

    this.dragState = {
      pointerId: event.pointerId,
      startX: event.clientX,
      scrollLeft: this.track.scrollLeft,
      moved: false,
    };

    this.track.classList.add('is-dragging');
    window.addEventListener('pointermove', this.onPointerMove, { passive: false });
    window.addEventListener('pointerup', this.onPointerUp, { once: true });
  }

  onPointerMove(event) {
    if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;
    const delta = event.clientX - this.dragState.startX;
    if (Math.abs(delta) > 4) this.dragState.moved = true;
    this.track.scrollLeft = this.dragState.scrollLeft - delta;
    if (this.dragState.moved) event.preventDefault();
  }

  onPointerUp(event) {
    if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;
    window.removeEventListener('pointermove', this.onPointerMove);
    this.track.classList.remove('is-dragging');

    const nearest = this.getNearestIndex();
    if (this.dragState.moved) this.ignoreClickUntil = Date.now() + 250;
    this.dragState = null;
    this.setActive(nearest, { scroll: true });
  }

  onScroll() {
    if (Date.now() < this.suppressScrollSyncUntil) return;
    if (this.programmaticScrollTimer) return;
    if (this.scrollRaf) return;
    this.scrollRaf = window.requestAnimationFrame(() => {
      this.scrollRaf = 0;
      this.setActive(this.getNearestIndex());
    });
  }

  onResize() {
    this.setActive(this.activeIndex < 0 ? 0 : this.activeIndex, { scroll: true, instant: true });
  }

  move(direction) {
    if (!this.items.length) return;
    const current = this.activeIndex < 0 ? this.getNearestIndex() : this.activeIndex;
    const next = Math.min(Math.max(current + direction, 0), this.items.length - 1);
    this.setActive(next, { scroll: true });
  }

  getNearestIndex() {
    if (!this.items.length) return 0;
    const trackRect = this.track.getBoundingClientRect();
    const trackCenter = trackRect.left + trackRect.width / 2;
    let nearestIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;

    this.items.forEach((item, index) => {
      const rect = item.getBoundingClientRect();
      const itemCenter = rect.left + rect.width / 2;
      const distance = Math.abs(trackCenter - itemCenter);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    return nearestIndex;
  }

  setActive(index, options = {}) {
    if (!this.items.length) return;
    const boundedIndex = Math.min(Math.max(index, 0), this.items.length - 1);
    const activeItem = this.items[boundedIndex];

    if (boundedIndex === this.activeIndex && !options.instant) return;
    this.activeIndex = boundedIndex;

    this.items.forEach((item, itemIndex) => {
      const isActive = itemIndex === boundedIndex;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-current', isActive ? 'true' : 'false');
      this.syncVideo(item, isActive);
    });

    if (options.scroll && activeItem) {
      window.requestAnimationFrame(() => {
        this.scrollToItem(activeItem, { instant: options.instant });
      });
    }
  }

  scrollToItem(item, options = {}) {
    if (!item || !this.track) return;

    const trackRect = this.track.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const currentScroll = this.track.scrollLeft;
    const itemCenterOffset = itemRect.left - trackRect.left + itemRect.width / 2;
    const target = currentScroll + itemCenterOffset - trackRect.width / 2;
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const clampedTarget = Math.min(Math.max(target, 0), Math.max(maxScroll, 0));

    const instant = options.instant || this.prefersReducedMotion;

    if (this.programmaticScrollTimer) window.clearTimeout(this.programmaticScrollTimer);
    this.suppressScrollSyncUntil = Date.now() + (instant ? 120 : 520);
    this.programmaticScrollTimer = window.setTimeout(() => {
      this.programmaticScrollTimer = 0;
    }, instant ? 80 : 360);

    this.track.scrollTo({
      left: clampedTarget,
      behavior: instant ? 'auto' : 'smooth',
    });
  }

  syncVideo(item, isActive) {
    const video = item.querySelector('video');
    if (!video) return;

    const autoplayAllowed = this.dataset.autoplay === 'true' && !this.prefersReducedMotion;

    if (!isActive || !this.isInView || !autoplayAllowed || item.hasAttribute('data-user-paused')) {
      video.pause();
      this.updateVideoControls(item);
      return;
    }

    this.playVideo(video);
    this.updateVideoControls(item);
  }

  updateVideoControls(item) {
    const video = item && item.querySelector('video');
    if (!video) return;

    const muteButton = item.querySelector('[data-video-action="mute"]');
    const playButton = item.querySelector('[data-video-action="play"]');

    item.setAttribute('data-video-muted', video.muted ? 'true' : 'false');
    item.setAttribute('data-video-paused', video.paused ? 'true' : 'false');

    if (muteButton) muteButton.setAttribute('aria-pressed', video.muted ? 'true' : 'false');
    if (playButton) playButton.setAttribute('aria-pressed', video.paused ? 'false' : 'true');
  }

  playVideo(video) {
    const promise = video.play();
    if (promise && typeof promise.then === 'function') {
      promise.catch(() => {});
    }
  }
}

if (!customElements.get('klerya-social-video-carousel')) {
  customElements.define('klerya-social-video-carousel', KleryaSocialVideoCarousel);
}
